/**
 * "How is this card usually paid?" — the credit card's repayment question (owner, 26 Oct 2026).
 *
 * A card has no term, so its repayment cannot be worked out, and a family member filling in the
 * profile rarely knows the figure. They usually do know HOW the card is paid, so that is what is asked;
 * the payment and the interest follow from it (shared/src/calculation/support/creditCards.ts, the same
 * rules the projection uses). Every answer, including "Not sure", gives a sensible figure — an empty
 * repayment box used to be modelled as a card that cost nothing.
 */
import React from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import {
    CARD_TYPICAL_RATE,
    cardPaymentStyleOf,
    cardPayoffSummary,
    cardRateIsTypical,
    type CardPaymentStyle,
} from '../../../../shared/src/calculation/support/creditCards.ts';

interface CardRow {
    id?: string;
    balance?: number | string;
    interestRate?: number | string | null;
    repayments?: number | string;
    frequency?: string;
    cardPaymentStyle?: unknown;
}

const OPTIONS: { value: CardPaymentStyle; label: string }[] = [
    { value: 'full', label: 'Paid off in full every month' },
    { value: 'minimum', label: 'Pays the minimum each month' },
    { value: 'fixed', label: 'Pays a set amount each month' },
    { value: 'unsure', label: 'Not sure' },
];

const money = (n: number) => formatCurrency(Math.round(n));

/** The one line under the answer: what that way of paying means for this card. */
export function cardConsequence(card: CardRow): string | null {
    const summary = cardPayoffSummary(card);
    if (summary.style === 'full') return 'No interest: it is cleared each month, and the spending is already in the living costs.';
    if (!(Number(card.balance) > 0)) return null;
    if (summary.style === 'fixed' && !(summary.monthlyPayment > 0)) return null;
    if (summary.grows) {
        return `At ${money(summary.monthlyPayment)} a month the balance keeps growing. The interest alone is about ${money(summary.monthlyInterest)} a month.`;
    }
    if (summary.months == null) return null;
    const years = summary.months / 12;
    const when = summary.months < 12
        ? `about ${summary.months} ${summary.months === 1 ? 'month' : 'months'}`
        : `about ${Math.round(years)} ${Math.round(years) === 1 ? 'year' : 'years'}`;
    const start = summary.style === 'fixed'
        ? `At ${money(summary.monthlyPayment)} a month`
        : `Paying the minimum (${money(summary.monthlyPayment)} this month, falling as the balance falls)`;
    return `${start}, this card is paid off in ${when} and costs about ${money(summary.totalInterest)} in interest.`;
}

interface Props {
    card: CardRow;
    onChange: (field: string, value: unknown) => void;
}

const CardPaymentQuestion = ({ card, onChange }: Props) => {
    const style = cardPaymentStyleOf(card);
    const consequence = cardConsequence(card);
    const groupName = `card-payment-${card.id ?? 'new'}`;
    return (
        <div className="md:col-span-2" data-testid="card-payment-question">
            <p className="block text-sm font-medium text-gray-700 mb-2">How is this card usually paid?</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="How is this card usually paid?">
                {OPTIONS.map((opt) => (
                    <label
                        key={opt.value}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-sm ${style === opt.value ? 'border-blue-600 text-slate-900' : 'border-slate-300 text-slate-700 hover:border-blue-300'}`}
                    >
                        <input
                            type="radio"
                            name={groupName}
                            value={opt.value}
                            checked={style === opt.value}
                            onChange={() => onChange('cardPaymentStyle', opt.value)}
                            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                            data-testid={`card-payment-${opt.value}`}
                        />
                        {opt.label}
                    </label>
                ))}
            </div>
            {style === 'fixed' && (
                <div className="mt-3 max-w-xs">
                    <CurrencyInput
                        label="How much each month"
                        name="repayments"
                        id={`liability-repayments-${card.id}`}
                        value={card.repayments || ''}
                        onChange={(v) => onChange('repayments', v)}
                    />
                </div>
            )}
            {style === 'unsure' && (
                <p className="mt-2 text-[13px] text-slate-600">We'll use the usual minimum. It's the "minimum payment" line on the statement.</p>
            )}
            {style !== 'full' && cardRateIsTypical(card) && (
                <p className="mt-2 text-[13px] text-slate-600" data-testid="card-typical-rate">
                    No interest rate entered, so we use {CARD_TYPICAL_RATE}%, a typical card rate. It's the "purchase rate" on the statement.
                </p>
            )}
            {consequence && (
                <p className="mt-2 text-[13px] font-medium text-slate-800" data-testid="card-consequence">{consequence}</p>
            )}
        </div>
    );
};

export default CardPaymentQuestion;
